import { useMemo } from "react";

import { useLocalization } from "@fluent/react";
import { TableSelect } from "@narmi/design_system";
import { useNotificationContext } from "cerulean";

import {
  useCorrectTransactionCategory,
  useIsCorrectingTaxonomy,
} from "../../hooks/useCorrectTransactionCategory";

import type { TransactionCategory } from "../../hooks/useTransactionCategories";

/** Stands in for "no category yet". Matches no taxonomy slug, so picking it is a no-op. */
const NO_CATEGORY = "__no_category__";

interface CategorySelectProps {
  transaction: API.Transaction;
  categories: TransactionCategory[];
  /**
   * DOM id for the control. The table cell and the details panel can show a select for the same
   * transaction at once, so each surface has to pass its own.
   */
  selectId: string;
}

const CategorySelect = ({
  transaction,
  categories,
  selectId,
}: CategorySelectProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();

  const { mutate } = useCorrectTransactionCategory({
    transactionId: transaction.id,
    onError: () => {
      sendNotification({
        type: "negative",
        text: l10n.getString("live-ledger-category-save-error"),
      });
    },
  });
  const isCorrecting = useIsCorrectingTaxonomy(transaction.id);

  // The endpoint returns the taxonomy in its own order. Copy before sorting, since this array is
  // the query cache's.
  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.label.localeCompare(b.label)),
    [categories]
  );

  const currentSlug = categories.find(
    (category) => category.label === transaction.category
  )?.slug;

  const handleChange = (slug: string) => {
    if (!slug || slug === currentSlug) return;
    const picked = categories.find((category) => category.slug === slug);
    if (!picked) return;
    mutate({ slug: picked.slug, label: picked.label });
  };

  const items = sortedCategories.map((category) => (
    <TableSelect.Item key={category.slug} value={category.slug}>
      {category.label}
    </TableSelect.Item>
  ));

  if (!currentSlug) {
    // With nothing selected TableSelect leaves the trigger blank, which in the details panel is an
    // empty box with no hint of what it is for. Picking this entry matches no slug, so it sends
    // nothing, and it drops out of the list once a real category is set.
    items.unshift(
      <TableSelect.Item key={NO_CATEGORY} value={NO_CATEGORY}>
        {l10n.getString("live-ledger-category-empty")}
      </TableSelect.Item>
    );
  }

  return (
    <TableSelect
      id={selectId}
      // Never drawn. TableSelect puts this on the trigger as aria-label, and since the design has
      // no visible label it is the control's only accessible name.
      label={l10n.getString("live-ledger-category-select-label")}
      value={currentSlug ?? NO_CATEGORY}
      onChange={handleChange}
      // Nothing cancels an in-flight correction, so a pick on either taxonomy control would race
      // one already running.
      isDisabled={isCorrecting}
    >
      {items}
    </TableSelect>
  );
};

export default CategorySelect;
